import { FC, useCallback, useEffect, useState } from "react";
import {
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Link,
    Stack,
    Typography
} from "@mui/material";
import { DoneAll, Inbox, MarkEmailRead } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import type { IMessage } from "@tintoa/identity-service-client";
import { adminApiClient } from "../utils/adminApiClient";
import { InboxListItem } from "../../components/InboxListItem.tsx";

const PAGE_SIZE = 10;

export const AdminInboxManagement: FC = () => {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [unread, setUnread] = useState<IMessage[]>([]);
    const [unreadTotal, setUnreadTotal] = useState(0);
    const [unreadOffset, setUnreadOffset] = useState(0);
    const [read, setRead] = useState<IMessage[]>([]);
    const [readTotal, setReadTotal] = useState(0);
    const [readOffset, setReadOffset] = useState(0);
    const [showRead, setShowRead] = useState(false);
    const [loadingUnread, setLoadingUnread] = useState(false);
    const [loadingRead, setLoadingRead] = useState(false);

    const loadUnread = useCallback(
        async (offset = 0, append = false) => {
            setLoadingUnread(true);
            try {
                const result = await adminApiClient.getRoleMessages({
                    read: false,
                    limit: PAGE_SIZE,
                    offset
                });
                setUnread((prev) =>
                    append ? [...prev, ...result.data] : result.data
                );
                setUnreadTotal(result.total);
                setUnreadOffset(offset + result.data.length);
            } catch (error) {
                console.error("Error loading unread role messages:", error);
                enqueueSnackbar("Posteingang konnte nicht geladen werden", {
                    variant: "error"
                });
            } finally {
                setLoadingUnread(false);
            }
        },
        [enqueueSnackbar]
    );

    const loadRead = useCallback(
        async (offset = 0, append = false) => {
            setLoadingRead(true);
            try {
                const result = await adminApiClient.getRoleMessages({
                    read: true,
                    limit: PAGE_SIZE,
                    offset
                });
                setRead((prev) =>
                    append ? [...prev, ...result.data] : result.data
                );
                setReadTotal(result.total);
                setReadOffset(offset + result.data.length);
            } catch (error) {
                console.error("Error loading read role messages:", error);
                enqueueSnackbar(
                    "Gelesene Nachrichten konnten nicht geladen werden",
                    {
                        variant: "error"
                    }
                );
            } finally {
                setLoadingRead(false);
            }
        },
        [enqueueSnackbar]
    );

    useEffect(() => {
        loadUnread(0, false);
    }, [loadUnread]);

    const markAsRead = async (id: string) => {
        try {
            await adminApiClient.markRoleMessageRead(id);
            setUnread((prev) => prev.filter((m) => m.id !== id));
            setUnreadTotal((prev) => Math.max(0, prev - 1));
            enqueueSnackbar("Nachricht als gelesen markiert", {
                variant: "info",
                autoHideDuration: 2500
            });
            if (showRead) {
                loadRead(0, false);
            }
        } catch (error) {
            console.error("Error marking role message as read:", error);
            enqueueSnackbar("Nachricht konnte nicht markiert werden", {
                variant: "error"
            });
        }
    };

    const markAllAsRead = async () => {
        try {
            await adminApiClient.markAllRoleMessagesRead();
            setUnread([]);
            setUnreadTotal(0);
            setUnreadOffset(0);
            enqueueSnackbar("Alle Nachrichten als gelesen markiert", {
                variant: "info",
                autoHideDuration: 2500
            });
            if (showRead) {
                loadRead(0, false);
            }
        } catch (error) {
            console.error("Error marking all role messages as read:", error);
            enqueueSnackbar("Nachrichten konnten nicht markiert werden", {
                variant: "error"
            });
        }
    };

    const formatDate = (date: Date | string) => {
        return new Intl.DateTimeFormat("de-DE", {
            dateStyle: "medium",
            timeStyle: "short"
        }).format(new Date(date));
    };

    const renderMessageItem = (message: IMessage, unreadItem: boolean) => (
        <InboxListItem
            key={message.id}
            app={message.clientApp}
            sourceLabel={message.clientApp?.name ?? "Anwendung"}
            title={message.title}
            body={message.content}
            footer={message.footer}
            link={message.link}
            linkTitle={message.linkTitle}
            date={formatDate(message.createdAt)}
            emphasized={unreadItem}
            dimmed={!unreadItem}
            actions={
                unreadItem ? (
                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<MarkEmailRead />}
                        onClick={() => markAsRead(message.id)}
                    >
                        Als gelesen markieren
                    </Button>
                ) : undefined
            }
        />
    );

    return (
        <Box sx={{ p: 3 }}>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link
                    color="inherit"
                    href="/app/profile"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate("/app/profile");
                    }}
                    sx={{
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" }
                    }}
                >
                    Profil
                </Link>
                <Link
                    color="inherit"
                    href="/app/admin"
                    onClick={(e) => {
                        e.preventDefault();
                        navigate("/app/admin");
                    }}
                    sx={{
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" }
                    }}
                >
                    Admin Dashboard
                </Link>
                <Typography color="text.primary">Posteingang</Typography>
            </Breadcrumbs>

            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    mb: 3
                }}
            >
                <Inbox sx={{ fontSize: 40, color: "primary.main" }} />
                <Box>
                    <Typography variant="h4" component="h1">
                        Posteingang
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Nachrichten an die tintoa-Administratoren
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    flexWrap: "wrap",
                    gap: 1
                }}
            >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Ungelesene Nachrichten
                </Typography>
                {unreadTotal > 0 && (
                    <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        startIcon={<DoneAll />}
                        onClick={markAllAsRead}
                    >
                        Alle als gelesen
                    </Button>
                )}
            </Box>

            {loadingUnread && unread.length === 0 ? (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 300
                    }}
                >
                    <CircularProgress />
                </Box>
            ) : (
                <Stack spacing={2}>
                    {unread.length > 0 ? (
                        unread.map((message) =>
                            renderMessageItem(message, true)
                        )
                    ) : (
                        <Card
                            variant="subtle"
                            sx={{ borderRadius: { xs: 0, sm: 2 } }}
                        >
                            <CardContent
                                sx={{
                                    p: { xs: 2, sm: 3 },
                                    textAlign: "center"
                                }}
                            >
                                <Inbox
                                    sx={{
                                        color: "text.secondary",
                                        fontSize: 64,
                                        mb: 2
                                    }}
                                />
                                <Typography
                                    variant="h6"
                                    color="text.secondary"
                                    gutterBottom
                                >
                                    Keine ungelesenen Nachrichten
                                </Typography>
                                <Typography color="text.secondary">
                                    Der Admin-Posteingang ist leer.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Stack>
            )}

            {unreadOffset < unreadTotal && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                    <Button
                        variant="outlined"
                        disabled={loadingUnread}
                        onClick={() => loadUnread(unreadOffset, true)}
                    >
                        {loadingUnread ? "Laden…" : "Weitere ungelesene laden"}
                    </Button>
                </Box>
            )}

            <Box sx={{ mt: 5, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Gelesene Nachrichten
                </Typography>
                {!showRead ? (
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setShowRead(true);
                            loadRead(0, false);
                        }}
                    >
                        Gelesene Nachrichten laden
                    </Button>
                ) : loadingRead && read.length === 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "center",
                            py: 4
                        }}
                    >
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        <Stack spacing={2}>
                            {read.length > 0 ? (
                                read.map((message) =>
                                    renderMessageItem(message, false)
                                )
                            ) : (
                                <Card
                                    variant="subtle"
                                    sx={{ borderRadius: { xs: 0, sm: 2 } }}
                                >
                                    <CardContent
                                        sx={{
                                            p: { xs: 2, sm: 3 },
                                            textAlign: "center"
                                        }}
                                    >
                                        <Typography color="text.secondary">
                                            Keine gelesenen Nachrichten.
                                        </Typography>
                                    </CardContent>
                                </Card>
                            )}
                        </Stack>
                        {readOffset < readTotal && (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    mt: 3
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    disabled={loadingRead}
                                    onClick={() => loadRead(readOffset, true)}
                                >
                                    {loadingRead
                                        ? "Laden…"
                                        : "Weitere gelesene Nachrichten laden"}
                                </Button>
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};
